import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Row, Col, Card, CardBody, Container, Spinner } from 'reactstrap';
import { useHistory } from 'react-router-dom';

import * as landingTemplatesActions from '../../store/landingTemplates/actions';
import * as countriesActions from '../../store/countries/actions';
import { useDispatch, useSelector } from 'react-redux';
import { actualCountry } from '../../selectors/country';

import Modal from '../../components/Common/Modal/Modal';
import ShowSingleElement from '../../components/Common/ShowSingleElement';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import DataTableNext from '../../components/Common/DataTable/DataTableNext';
import createTableColumns from '../../components/LandingTemplates/tableColumns';
import {
  CreateButton,
  ExportButtonPlacement,
  ToggleColumnButton,
} from '../../components/Common/DataTable/tableActionButtons';
import { addOrRemove } from '../../helpers/sharedFunction';

const List = () => {
  const loading = useSelector((state) => state.LandingTemplate.loading);
  const landingTemplates = useSelector(
    (state) => state.LandingTemplate.landingTemplates,
  );
  const countries = useSelector((state) => state.Country.countries);
  const country = useSelector(actualCountry);
  const countryId = country ? country.id : null;

  const [viewModal, setViewModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    dispatch(countriesActions.syncCountries());
  }, [dispatch]);

  useEffect(() => {
    if (countryId) {
      dispatch(landingTemplatesActions.syncLandingTemplates());
    }

    return () => {
      dispatch(landingTemplatesActions.resetState());
    };
  }, [dispatch, countryId]);

  const openEditModal = useCallback((template = null) => {
    if (template && template.id) {
      history.push(`/landing-templates/${template.id}/edit`);
    } else {
      history.push('/landing-templates/new');
    }
  }, [history]);

  const openViewModal = useCallback((template) => {
    setSelectedTemplate(template);
    setViewModal(true);
  }, []);

  const openArchiveModal = useCallback((template) => {
    dispatch(landingTemplatesActions.archiveLandingTemplate(template.id));
  }, [dispatch]);

  const openDuplicate = useCallback(
    (template) => {
      history.push('/landing-templates/new', { duplicateFrom: template.id });
    },
    [history],
  );

  const columns = useMemo(
    () =>
      createTableColumns(
        openViewModal,
        openEditModal,
        openArchiveModal,
        countries,
        openDuplicate,
      ),
    [countries, openArchiveModal, openEditModal, openViewModal, openDuplicate],
  );
  const [hiddenColumns, setHiddenColumns] = useState(
    columns.filter(({ hidden }) => !!hidden).map(({ name }) => name),
  );
  const handleToggleColumn = (name) => {
    setHiddenColumns(addOrRemove(hiddenColumns, name));
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Landing Templates" loading={loading} />
          <Row>
            <Col className="col-12">
              <Card>
                <CardBody>
                  <Row className="align-items-center justify-content-end mb-2">
                    <Spinner
                      color="secondary"
                      animation="border"
                      hidden={!loading}
                    />
                    <CreateButton
                      label="Create new Template"
                      disabled={loading}
                      onPress={() => openEditModal()}
                    />
                    <ToggleColumnButton
                      columns={columns}
                      hiddenColumns={hiddenColumns}
                      onToggleColumn={handleToggleColumn}
                    />
                    <ExportButtonPlacement />
                  </Row>
                  <DataTableNext
                    loading={loading}
                    rows={landingTemplates}
                    columns={columns}
                    filters={filters}
                    onFiltersChange={setFilters}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    currentPage={currentPage}
                    onCurrentPageChange={setCurrentPage}
                    hiddenColumns={hiddenColumns}
                    onHiddenColumnsChange={setHiddenColumns}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Modal
            size="lg"
            title="Landing Template"
            isOpen={viewModal}
            toggle={() => setViewModal(!viewModal)}
            showClose
            scrollable={false}>
            <ShowSingleElement
              element={selectedTemplate}
              collection="landingTemplates"
              icon="bx bx-layout"
            />
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default List;
